/**
 * Activity Repository — CRUD untuk tabel activity_cache.
 *
 * Entitas inti EKINKINIAN. Setiap operasi write menandai sync_status = 'pending'
 * agar sync engine (S14) tahu data perlu dikirim ke cloud.
 *
 * Acuan: docs/04-Database.md §6 (activity_cache), docs/03-Workflow.md §7.1 (status).
 */

import type { SqlValue } from 'sql.js';
import { BaseRepository } from '../repository';
import type { ActivityCache, ActivityStatus, Priority, SyncStatus } from '../types';

/** Input untuk membuat aktivitas baru (field auto-generated dikecualikan). */
export interface CreateActivityInput {
  tenant_id: string;
  workspace_id: string;
  category_id?: string | null;
  template_id?: string | null;
  title: string;
  description?: string | null;
  activity_date: string;
  start_time?: string | null;
  end_time?: string | null;
  duration_minutes?: number | null;
  location?: string | null;
  status?: ActivityStatus;
  priority?: Priority;
  metadata?: string;
  created_by: string;
}

/** Input untuk update aktivitas (tenant_id & created_by tidak bisa diubah). */
export type UpdateActivityInput = Partial<
  Omit<CreateActivityInput, 'tenant_id' | 'workspace_id' | 'created_by'>
>;

export class ActivityRepository extends BaseRepository<ActivityCache> {
  protected readonly tableName = 'activity_cache';

  protected mapRow(row: Record<string, unknown>): ActivityCache {
    return row as unknown as ActivityCache;
  }

  // -------------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------------

  async create(data: CreateActivityInput): Promise<ActivityCache> {
    const id = this.generateId();
    const now = this.now();

    this.insertRow({
      id,
      tenant_id: data.tenant_id,
      workspace_id: data.workspace_id,
      category_id: data.category_id ?? null,
      template_id: data.template_id ?? null,
      title: data.title,
      description: data.description ?? null,
      activity_date: data.activity_date,
      start_time: data.start_time ?? null,
      end_time: data.end_time ?? null,
      duration_minutes: data.duration_minutes ?? null,
      location: data.location ?? null,
      status: data.status ?? 'draft',
      priority: data.priority ?? 'normal',
      metadata: data.metadata ?? '{}',
      created_by: data.created_by,
      updated_by: null,
      sync_status: 'pending',
      last_synced_at: null,
      created_at: now,
      updated_at: now,
      deleted_at: null,
    });

    const created = await this.findById(id);
    if (!created) throw new Error(`Failed to create activity: ${id}`);
    return created;
  }

  // -------------------------------------------------------------------------
  // Update
  // -------------------------------------------------------------------------

  async update(id: string, changes: UpdateActivityInput): Promise<ActivityCache | null> {
    const updateData: Record<string, SqlValue> = { updated_at: this.now() };

    if (changes.category_id !== undefined) updateData.category_id = changes.category_id;
    if (changes.template_id !== undefined) updateData.template_id = changes.template_id;
    if (changes.title !== undefined) updateData.title = changes.title;
    if (changes.description !== undefined) updateData.description = changes.description;
    if (changes.activity_date !== undefined) updateData.activity_date = changes.activity_date;
    if (changes.start_time !== undefined) updateData.start_time = changes.start_time;
    if (changes.end_time !== undefined) updateData.end_time = changes.end_time;
    if (changes.duration_minutes !== undefined)
      updateData.duration_minutes = changes.duration_minutes;
    if (changes.location !== undefined) updateData.location = changes.location;
    if (changes.status !== undefined) updateData.status = changes.status;
    if (changes.priority !== undefined) updateData.priority = changes.priority;
    if (changes.metadata !== undefined) updateData.metadata = changes.metadata;

    // Setiap update lokal menandai perlu sync ulang.
    updateData.sync_status = 'pending';

    this.updateRow(id, updateData);
    return this.findById(id);
  }

  // -------------------------------------------------------------------------
  // Specialized Queries
  // -------------------------------------------------------------------------

  /**
   * Ambil semua aktivitas dalam workspace (tidak ter-soft-delete),
   * diurutkan berdasarkan tanggal terbaru.
   */
  async findByWorkspace(workspaceId: string, includeDeleted = false): Promise<ActivityCache[]> {
    let sql = `SELECT * FROM ${this.tableName} WHERE workspace_id = ?`;
    const params: SqlValue[] = [workspaceId];

    if (!includeDeleted) {
      sql += ` AND deleted_at IS NULL`;
    }
    sql += ` ORDER BY activity_date DESC, created_at DESC`;

    const rows = this.db.queryAll<Record<string, unknown>>(sql, params);
    return rows.map((row) => this.mapRow(row));
  }

  /**
   * Ambil aktivitas dalam rentang tanggal.
   * @param startDate ISO date (inclusive)
   * @param endDate ISO date (inclusive)
   */
  async findByDateRange(
    workspaceId: string,
    startDate: string,
    endDate: string,
  ): Promise<ActivityCache[]> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
        AND activity_date >= ?
        AND activity_date <= ?
        AND deleted_at IS NULL
      ORDER BY activity_date ASC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId, startDate, endDate]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Ambil semua aktivitas dengan sync_status tertentu (untuk sync engine). */
  async findBySyncStatus(status: SyncStatus): Promise<ActivityCache[]> {
    const sql = `SELECT * FROM ${this.tableName} WHERE sync_status = ?`;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [status]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Tandai aktivitas sebagai tersinkron (dipanggil setelah sync berhasil). */
  async markSynced(id: string): Promise<void> {
    this.updateRow(id, {
      sync_status: 'synced',
      last_synced_at: this.now(),
    });
  }

  // -------------------------------------------------------------------------
  // Soft Delete & Restore (UC-04 A2/A3)
  // -------------------------------------------------------------------------

  /**
   * Soft delete — tandai deleted_at + set sync_status='pending'.
   * Aktivitas disembunyikan dari daftar default tapi tetap di DB (BR-11).
   */
  async softDelete(id: string): Promise<void> {
    const now = this.now();
    this.updateRow(id, {
      deleted_at: now,
      updated_at: now,
      sync_status: 'pending',
    });
  }

  /**
   * Restore — batalkan soft delete (UC-04 A3).
   * Set deleted_at = NULL + sync_status='pending'.
   */
  async restore(id: string): Promise<void> {
    this.updateRow(id, {
      deleted_at: null,
      updated_at: this.now(),
      sync_status: 'pending',
    });
  }

  // -------------------------------------------------------------------------
  // Search & Filter (UC-05, FR-ACT-02, FR-ACT-06)
  // -------------------------------------------------------------------------

  /**
   * Pencarian full-text pada judul + deskripsi (FR-ACT-06).
   * Menggunakan SQLite LIKE (case-insensitive).
   * @param workspaceId ID workspace.
   * @param query Kata kunci pencarian.
   */
  async search(workspaceId: string, query: string): Promise<ActivityCache[]> {
    const pattern = `%${query.trim()}%`;
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
        AND deleted_at IS NULL
        AND (title LIKE ? OR description LIKE ?)
      ORDER BY activity_date DESC, created_at DESC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId, pattern, pattern]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Ambil aktivitas berdasarkan status (FR-ACT-02). */
  async findByStatus(workspaceId: string, status: ActivityStatus): Promise<ActivityCache[]> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
        AND status = ?
        AND deleted_at IS NULL
      ORDER BY activity_date DESC, created_at DESC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId, status]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Ambil aktivitas berdasarkan kategori (FR-ACT-02). */
  async findByCategory(workspaceId: string, categoryId: string): Promise<ActivityCache[]> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
        AND category_id = ?
        AND deleted_at IS NULL
      ORDER BY activity_date DESC, created_at DESC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId, categoryId]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Ambil aktivitas yang sudah di-soft-delete (trash / tempat sampah). */
  async findDeleted(workspaceId: string): Promise<ActivityCache[]> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
        AND deleted_at IS NOT NULL
      ORDER BY deleted_at DESC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Hitung jumlah aktivitas (tidak ter-delete) dalam workspace. */
  async countByWorkspace(workspaceId: string): Promise<number> {
    return this.count({
      workspace_id: workspaceId,
      deleted_at: null,
    });
  }
}
